import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Menu,
  X,
  Phone,
  ChevronDown,
  Heart,
  Stethoscope,
  Home,
  Users,
  BookOpen,
  Mail,
  MessageCircle
} from 'lucide-react'
import { contactInfo } from '@/data/contact'
import { cn } from '@/utils/cn'
import { toTelHref, toWhatsAppHref } from '@/utils/phone'

const Header: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
  const location = useLocation()

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const navItems = [
    { 
      name: 'Ana Sayfa', 
      href: '/', 
      icon: Home 
    },
    {
      name: 'Şanlıurfa Veteriner',
      href: '/sanliurfa-veteriner',
      icon: Heart,
      dropdown: [
        { name: 'Şanlıurfa Veteriner', href: '/sanliurfa-veteriner' },
        { name: 'Karaköprü Veteriner', href: '/karakopru-veteriner' },
        { name: '7/24 Acil Veteriner', href: '/acil-veteriner' }
      ]
    },
    { 
      name: 'Bizi Tanıyın', 
      href: '/about', 
      icon: Users 
    },
    {
      name: 'Kliniğimiz',
      href: '#',
      icon: Stethoscope,
      dropdown: [
        { name: 'Müdahale ve Girişim Alanları', href: '/klinik/mudahale-ve-girisim-alanlari' },
        { name: 'Muayene Odaları', href: '/klinik/muayene-odalari' },
        { name: 'Ameliyathane', href: '/klinik/ameliyathane' },
        { name: 'Tıbbi Görüntüleme', href: '/klinik/tibbi-goruntuleme' },
        { name: 'Klinik Laboratuvar', href: '/klinik/klinik-laboraturvar' },
        { name: 'Eğitim ve Danışan Alanları', href: '/klinik/egitim-ve-danisan-alanlari' },
        { name: 'Pet Oteli', href: '/klinik/pet-oteli' },
        { name: 'Pet Kuaför', href: '/klinik/pet-kuafor' }
      ]
    },
    {
      name: 'Klinik Hizmetler',
      href: '#',
      icon: Heart,
      dropdown: [
        { name: 'Acil Tedavi', href: '/hizmetler/acil-tedavi' },
        { name: 'Ağız ve Diş', href: '/hizmetler/agiz-ve-dis' },
        { name: 'Cerrahi', href: '/hizmetler/cerrahi' },
        { name: 'Dahiliye', href: '/hizmetler/dahiliye' },
        { name: 'Dermatoloji', href: '/hizmetler/dermatoloji' },
        { name: 'Endoskopi', href: '/hizmetler/endoskopi' },
        { name: 'Evde Veteriner', href: '/hizmetler/evde-veterinerlik-hizmeti' },
        { name: 'Kardiyoloji', href: '/hizmetler/kardiyoloji' },
        { name: 'Check-Up', href: '/hizmetler/check-up' },
        { name: 'Koruyucu Hekimlik', href: '/hizmetler/koruyucu-hekimlik' }
      ]
    },
    {
      name: 'Kedi Sağlığı',
      href: '/kedi',
      icon: BookOpen,
      dropdown: [
        { name: 'Kedi Sağlığı — Genel', href: '/kedi' },
        { name: 'Kedi Aşı Takvimi', href: '/kedi/asi-takvimi' },
        { name: 'Kedi Kısırlaştırma', href: '/kedi/kisirlastirma' },
        { name: 'Kedilerde Parazit Korunması', href: '/kedi/parazit-korunmasi' },
        { name: 'Kedi Hastalıkları', href: '/hastaliklari/kedi' }
      ]
    },
    {
      name: 'Köpek Sağlığı',
      href: '/kopek',
      icon: BookOpen,
      dropdown: [
        { name: 'Köpek Sağlığı — Genel', href: '/kopek' },
        { name: 'Köpek Aşı Takvimi', href: '/kopek/asi-takvimi' },
        { name: 'Köpek Kısırlaştırma', href: '/kopek/kisirlastirma' },
        { name: 'Köpeklerde Parazit Korunması', href: '/kopek/parazit-korunmasi' },
        { name: 'Köpek Hastalıkları', href: '/hastaliklari/kopek' }
      ]
    },
    { 
      name: 'Can Dostlarımız', 
      href: '/pets', 
      icon: Heart 
    },
    { 
      name: 'İletişim', 
      href: '/contact', 
      icon: Mail 
    }
  ]

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-500',
        isScrolled 
          ? 'bg-white/98 backdrop-blur-xl shadow-2xl border-b border-gray-200' 
          : 'bg-white/80 backdrop-blur-sm'
      )}
    >
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <motion.img
              whileHover={{ scale: 1.05 }}
              src="/logo.webp"
              alt="Ardıç Veteriner Kliniği"
              className="h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden xl:flex items-center space-x-2">
            {navItems.map((item) => (
              <div
                key={item.name}
                className="relative"
                onMouseEnter={() => item.dropdown && setActiveDropdown(item.name)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                {item.dropdown ? (
                  <button
                    className={cn(
                      'flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-300',
                      'hover:bg-primary-50 hover:text-primary-600 hover:shadow-md',
                      'whitespace-nowrap text-sm font-medium',
                      location.pathname.startsWith(item.href) && item.href !== '#'
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-gray-700'
                    )}
                  >
                    <item.icon className="w-4 h-4 flex-shrink-0" />
                    <span>{item.name}</span>
                    <ChevronDown className="w-3 h-3 flex-shrink-0" />
                  </button>
                ) : (
                  <Link
                    to={item.href}
                    className={cn(
                      'flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-300',
                      'hover:bg-primary-50 hover:text-primary-600 hover:shadow-md',
                      'whitespace-nowrap text-sm font-medium',
                      location.pathname === item.href
                        ? 'bg-primary-100 text-primary-700 shadow-md'
                        : 'text-gray-700'
                    )}
                  >
                    <item.icon className="w-4 h-4 flex-shrink-0" />
                    <span>{item.name}</span>
                  </Link>
                )}

                {/* Dropdown Menu */}
                <AnimatePresence>
                  {item.dropdown && activeDropdown === item.name && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-full left-0 mt-2 w-72 bg-white rounded-xl shadow-2xl border border-gray-100 py-3 z-50 backdrop-blur-sm"
                    >
                      {item.dropdown.map((dropdownItem, index) => (
                        <motion.div
                          key={dropdownItem.name}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.2, delay: index * 0.05 }}
                        >
                          <Link
                            to={dropdownItem.href}
                            className="block px-4 py-3 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-600 transition-all duration-200 hover:translate-x-1"
                          >
                            {dropdownItem.name}
                          </Link>
                        </motion.div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </nav>

          {/* Contact Buttons & Mobile Menu Toggle */}
          <div className="flex items-center space-x-3">
            {/* Phone Button */}
            <motion.a
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              href={toTelHref(contactInfo.phone)}
              className="hidden sm:flex items-center space-x-2 bg-primary-500 hover:bg-primary-600 text-white px-3 py-2 rounded-lg font-medium transition-colors duration-200 text-sm"
            >
              <Phone className="w-4 h-4" />
              <span className="hidden md:block">Ara</span>
            </motion.a>

            {/* WhatsApp Button */}
            <motion.a
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              href={toWhatsAppHref(contactInfo.whatsapp)}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:flex items-center space-x-2 bg-green-500 hover:bg-green-600 text-white px-3 py-2 rounded-lg font-medium transition-colors duration-200 text-sm"
            >
              <MessageCircle className="w-4 h-4" />
              <span className="hidden md:block">WhatsApp</span>
            </motion.a>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="xl:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="xl:hidden bg-white border-t border-gray-100 shadow-lg"
          >
            <div className="container-custom py-4">
              <nav className="space-y-2">
                {navItems.map((item) => (
                  <div key={item.name}>
                    {item.dropdown ? (
                      <div>
                        <button
                          onClick={() => setActiveDropdown(
                            activeDropdown === item.name ? null : item.name
                          )}
                          className="flex items-center justify-between w-full px-4 py-3 text-left text-gray-700 hover:bg-primary-50 rounded-lg transition-colors duration-200"
                        >
                          <div className="flex items-center space-x-3">
                            <item.icon className="w-5 h-5" />
                            <span className="font-medium">{item.name}</span>
                          </div>
                          <ChevronDown
                            className={cn(
                              'w-4 h-4 transition-transform duration-200',
                              activeDropdown === item.name && 'rotate-180'
                            )}
                          />
                        </button>
                        <AnimatePresence>
                          {activeDropdown === item.name && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="ml-8 mt-2 space-y-1"
                            >
                              {item.dropdown.map((dropdownItem) => (
                                <Link
                                  key={dropdownItem.name}
                                  to={dropdownItem.href}
                                  onClick={() => setIsOpen(false)}
                                  className="block px-4 py-2 text-sm text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors duration-200"
                                >
                                  {dropdownItem.name}
                                </Link>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ) : (
                      <Link
                        to={item.href}
                        onClick={() => setIsOpen(false)}
                        className={cn(
                          'flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors duration-200',
                          location.pathname === item.href
                            ? 'bg-primary-100 text-primary-700'
                            : 'text-gray-700 hover:bg-primary-50'
                        )}
                      >
                        <item.icon className="w-5 h-5" />
                        <span className="font-medium">{item.name}</span>
                      </Link>
                    )}
                  </div>
                ))}
              </nav>

              {/* Mobile Contact Buttons */}
              <div className="grid grid-cols-1 gap-3 mt-4">
                <motion.a
                  whileTap={{ scale: 0.95 }}
                  href={toTelHref(contactInfo.phone)}
                  className="flex items-center justify-center space-x-2 w-full bg-primary-500 text-white py-3 rounded-lg font-medium"
                >
                  <Phone className="w-5 h-5" />
                  <span>Telefon</span>
                </motion.a>
                
                <motion.a
                  whileTap={{ scale: 0.95 }}
                  href={toWhatsAppHref(contactInfo.whatsapp)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center space-x-2 w-full bg-green-500 text-white py-3 rounded-lg font-medium"
                >
                  <MessageCircle className="w-5 h-5" />
                  <span>WhatsApp</span>
                </motion.a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  )
}

export default Header
